//
// Pure compiler: ReactFlow graph -> { uniforms, chunks, defines, errors }.
// Walks back from the single PrincipledBSDF output node in topological order,
// emits one GLSL statement per node, and wires each surface slot the output
// node consumes into a chunk keyed by slot name. No THREE, no DOM.

import { NODE_TYPES } from './materialNodes.js'

const GLSL_TYPE = { float: 'float', vec2: 'vec2', vec3: 'vec3', vec4: 'vec4' }

// Sanitize a string for use in GLSL identifiers (remove special chars).
function sanitize(s) { return String(s).replace(/[^a-zA-Z0-9_]/g, '_') }

// Map an output-node input handle -> the three.js fragment-shader target stmt.
// `expr` is the GLSL var/literal feeding the slot.
const SLOT_TARGET = {
  color:     (e) => `diffuseColor.rgb = ${e};`,
  metalness: (e) => `metalnessFactor = ${e};`,
  roughness: (e) => `roughnessFactor = ${e};`,
  emissive:  (e) => `totalEmissiveRadiance += ${e};`,
  opacity:   (e) => `diffuseColor.a = ${e};`,
  // AO darkens indirect (ambient / IBL) light, mirroring three's <aomap_fragment>:
  // indirectDiffuse *= ao, indirectSpecular *= computeSpecularOcclusion(...).
  // 1.0 = no occlusion (so the default is a no-op). Injected after <aomap_fragment>
  // where reflectedLight.indirect* / geometryNormal / geometryViewDir all exist.
  ao:        (e) => `reflectedLight.indirectDiffuse *= ${e};\n  reflectedLight.indirectSpecular *= computeSpecularOcclusion( saturate( dot( geometryNormal, geometryViewDir ) ), ${e}, material.roughness );`,
  normal:    (e) => `normal = normalize(${e});`,
  // Specular = dielectric reflectance 0..1, Unreal's DielectricSpecularToF0:
  // F0 = 0.08 * specular (so 0.5 = three's default 4%). `material.specularColor`
  // (the dielectric F0) is set up by <lights_physical_fragment> (Standard +
  // Physical); we overwrite it after, then refresh specularColorBlended (what the
  // lighting loop reads). F90 is left at three's default — Specular only drives F0.
  specular:      (e) => `material.specularColor = vec3(0.08 * ${e});\n  material.specularColorBlended = mix(material.specularColor, diffuseColor.rgb, metalnessFactor);`,
}

// Coerce a GLSL expression between scalar/vector types so any output can feed
// any input without a dimension-mismatch compile error (e.g. a Fresnel float
// drives a vec3 emissive, a Component Mask vec2 drives a float roughness).
//   scalar -> vecN : replicate          vecN -> scalar : take .x
//   shrink (vec3->vec2) : swizzle        grow (vec2->vec3) : pad with 0 (w pads 1)
const DIM = { float: 1, vec2: 2, vec3: 3, vec4: 4 }
function coerce(expr, fromType, toType) {
  if (fromType === toType) return expr
  const f = DIM[fromType] ?? 1
  const t = DIM[toType] ?? 1
  if (f === t) return expr
  if (f === 1) return `${toType}(${expr})`          // scalar -> vecN
  if (t === 1) return `(${expr}).x`                 // vecN -> scalar
  if (t < f) return `(${expr}).${['x', 'xy', 'xyz'][t - 1]}` // shrink
  if (f === 2 && t === 3) return `vec3(${expr}, 0.0)`
  if (f === 2 && t === 4) return `vec4(${expr}, 0.0, 0.0)`
  if (f === 3 && t === 4) return `vec4(${expr}, 1.0)`
  return expr
}

// Pure: list explicitly-exposed inputs of a graph as a flat param list.
// Shape: [{ nodeId, handle, type, name, value }]. Used by the inspector.
export function collectExposedParams(graph) {
  const out = []
  const seen = new Set() // dedupe by `${type}:${name}` — same name+type = same param
  for (const n of graph?.nodes ?? []) {
    const def = NODE_TYPES[n.type]
    // Unreal-style parameter nodes (a Constant or Texture Sample "Converted to
    // Parameter"). Texture params carry a textureId; numeric params a value.
    if (n.data?.param) {
      const isTexture = !!def?.hasTexture
      const type = isTexture ? 'texture' : (def?.outputs?.[0]?.type ?? 'float')
      const name = (n.data.paramName != null && String(n.data.paramName).trim()) || 'Param'
      const key = `${type}:${name}`
      if (!seen.has(key)) {
        seen.add(key)
        const value = isTexture
          ? (n.data.textureId ?? null)
          : (n.data.value ?? (type === 'float' ? 0 : [0, 0, 0]))
        out.push({ nodeId: n.id, handle: isTexture ? 'textureId' : 'value', type, name, value })
      }
      continue
    }
    // Legacy per-input expose (existing materials authored before parameters).
    const expose = n.data?.expose
    if (!expose) continue
    for (const [handle, name] of Object.entries(expose)) {
      const inputDef = def?.inputs?.find(i => i.handle === handle)
      const type = inputDef?.type ?? 'float'
      const value = n.data?.[handle] ?? inputDef?.default ?? (type === 'float' ? 0 : [0, 0, 0])
      out.push({ nodeId: n.id, handle, type, name, value })
    }
  }
  return out
}

function literal(value, type) {
  if (type === 'float') return Number(value ?? 0).toFixed(6)
  const a = Array.isArray(value) ? value : [value, value, value]
  if (type === 'vec3') return `vec3(${a.slice(0, 3).map(n => Number(n).toFixed(6)).join(', ')})`
  if (type === 'vec2') return `vec2(${a.slice(0, 2).map(n => Number(n).toFixed(6)).join(', ')})`
  return `vec4(${a.slice(0, 4).map(n => Number(n).toFixed(6)).join(', ')})`
}

export function compileMaterialGraph(graph) {
  const errors = []
  const nodes = graph?.nodes ?? []
  const edges = graph?.edges ?? []
  const byId = new Map(nodes.map(n => [n.id, n]))

  const outNode = nodes.find(n => NODE_TYPES[n.type]?.isOutput)
  if (!outNode) {
    return { uniforms: {}, chunks: {}, defines: {}, errors: ['no PrincipledBSDF output node'], params: [], shaderModel: 'standard' }
  }

  const shaderModel = NODE_TYPES[outNode.type]?.shaderModel ?? 'standard'

  // edges feeding a given (target, handle): which (source, sourceHandle) drives it.
  const incoming = (id, handle) =>
    edges.find(e => e.target === id && e.targetHandle === handle)

  const uniforms = {}
  const params = []
  let uid = 0
  const uniqueName = (base) => `u_${base}_${uid++}`
  let needsTime = false

  // Emitted GLSL statements (topo order) + per-node output var names.
  const stmts = []
  const emittedVarByNodeHandle = new Map() // `${nodeId}:${handle}` -> { name, type }
  const visiting = new Set()
  const done = new Set()
  // A TextureSample with no wired `uv` samples at `vUv`, which MeshStandard only
  // declares when USE_UV is defined. Flag it so the material builder sets the
  // define — otherwise the fragment shader fails to compile (undeclared vUv).
  let needsUv = false
  // World Position / World Normal nodes read varyings that MeshStandard doesn't
  // provide. When set, the material builder also patches the vertex shader to
  // compute + pass vGraphWorldPos / vGraphWorldNormal (see assembleGraphVertex).
  let needsWorld = false
  // A GaussianColor node reads the per-Gaussian vGaussianColor attribute the
  // splat shading path supplies. When set, assembleGraphShader declares a white
  // fallback so a mesh material (which has no such attribute) still compiles.
  let needsSplatColor = false
  // Top-level GLSL helper functions a node needs (e.g. noise). Injected once at
  // global scope — functions can't be declared inside the per-slot {} chunks.
  const helpers = new Set()

  // Emit `node` (and its deps) once; outputs recorded in emittedVarByNodeHandle.
  function emitNode(node) {
    if (done.has(node.id)) return
    if (visiting.has(node.id)) { errors.push(`cycle through node ${node.id}`); return }
    visiting.add(node.id)

    const def = NODE_TYPES[node.type]
    if (!def) { errors.push(`unknown node type ${node.type}`); visiting.delete(node.id); return }

    if (node.type === 'Panner' || node.type === 'Time') needsTime = true
    if (node.type === 'UV') needsUv = true
    if ((node.type === 'TextureSample' || node.type === 'Noise' || node.type === 'NormalMap' || node.type === 'Panner') && !incoming(node.id, 'uv')) needsUv = true
    if (node.type === 'WorldPosition' || node.type === 'WorldNormal' || node.type === 'ViewDirection') needsWorld = true
    if (node.type === 'GaussianColor') needsSplatColor = true

    const ctx = {
      node,
      varOf(handle) {
        const inputDef = def.inputs.find(i => i.handle === handle)
        const edge = incoming(node.id, handle)
        if (edge) {
          const src = byId.get(edge.source)
          if (src) {
            emitNode(src)
            const rec = emittedVarByNodeHandle.get(`${src.id}:${edge.sourceHandle}`)
            if (rec) return coerce(rec.name, rec.type, inputDef?.type ?? rec.type)
          }
        }
        // Unconnected -> inline value from node.data, else the input default.
        const raw = node.data?.[handle] ?? inputDef?.default
        if (raw == null) return literal(inputDef?.type === 'float' ? 0 : [0, 0, 0], inputDef?.type ?? 'float')
        // Check if this input is explicitly exposed as a graph parameter.
        const exposedName = node.data?.expose?.[handle]
        if (exposedName) {
          const name = `p_${sanitize(node.id)}_${sanitize(handle)}`
          uniforms[name] = { value: (inputDef?.type === 'float') ? Number(raw) : raw, type: inputDef?.type ?? 'float' }
          if (!params.some(p => p.uniform === name)) {
            params.push({ uniform: name, nodeId: node.id, handle, type: inputDef?.type ?? 'float', name: exposedName, value: (inputDef?.type === 'float') ? Number(raw) : raw })
          }
          return name
        }
        // Promote scalars/colors to uniforms so they're live-tweakable.
        const name = uniqueName(handle)
        uniforms[name] = { value: (inputDef?.type === 'float') ? Number(raw) : raw, type: inputDef?.type ?? 'float' }
        return name
      },
      hasInput(handle) { return !!incoming(node.id, handle) },
      uniform(base, value) {
        if (base === 'time_marker') { needsTime = true; return 'uTime' }
        const n = uniqueName(base)
        const type = Array.isArray(value) ? (value.length === 2 ? 'vec2' : value.length === 4 ? 'vec4' : 'vec3') : 'float'
        uniforms[n] = { value, type }
        return n
      },
      // A named, Unreal-style material parameter. Keyed by NAME (+type), so two
      // converted constants sharing a name resolve to ONE uniform → same value.
      namedParam(rawName, value, type) {
        const pname = (rawName != null && String(rawName).trim()) || 'Param'
        const name = `p_${sanitize(pname)}`
        uniforms[name] = { value: type === 'float' ? Number(value) : value, type }
        if (!params.some(p => p.uniform === name)) {
          params.push({ uniform: name, nodeId: node.id, handle: 'value', type, name: pname, value })
        }
        return name
      },
      uniformSampler(base, textureId, opts) {
        const n = uniqueName(base)
        // Default to null (not undefined) so this is always recognized as a
        // sampler2D uniform even before a texture is picked — otherwise the
        // type inference declares it `float` and texture2D() fails to compile.
        // opts.linear loads the texture as NoColorSpace (normal/data maps).
        uniforms[n] = { value: null, textureId: textureId ?? null, linear: !!opts?.linear }
        return n
      },
      // A named texture parameter (Unreal TextureSampleParameter2D). Keyed by
      // NAME, so two converted Texture Samples sharing a name share one sampler.
      namedSampler(rawName, textureId, opts) {
        const pname = (rawName != null && String(rawName).trim()) || 'Param'
        const name = `ps_${sanitize(pname)}`
        uniforms[name] = { value: null, textureId: textureId ?? null, linear: !!opts?.linear }
        if (!params.some(p => p.uniform === name)) {
          // handle 'textureId' matches the node-data field the editor/inspector
          // writes, so editing the param targets the right binding.
          params.push({ uniform: name, nodeId: node.id, handle: 'textureId', type: 'texture', name: pname, value: textureId ?? null })
        }
        return name
      },
      out(handle, type) {
        const name = `v_${node.id}_${handle}`.replace(/[^a-zA-Z0-9_]/g, '_')
        emittedVarByNodeHandle.set(`${node.id}:${handle}`, { name, type: GLSL_TYPE[type] ?? 'float' })
        return name
      },
      // Request a top-level GLSL helper function (see HELPER_GLSL). Returns the
      // helper key so emit() reads naturally: ctx.helper('noise2').
      helper(name) { helpers.add(name); return name },
    }

    if (typeof def.emit === 'function') {
      const s = def.emit(ctx)
      if (s) stmts.push(s)
    }
    visiting.delete(node.id)
    done.add(node.id)
  }

  // Build a chunk per surface slot the output node consumes.
  const chunks = {}
  // Per-slot pre-target expression: { stmts, expr, type }. The three.js path
  // wraps these into `chunks` with a MeshStandard target assignment; the splat
  // param-bake path (assembleSplatBakeShader) targets its own MRT outputs from
  // the same expressions — "one registry, two assemblers" (SPLATS-MATERIAL.md
  // Task 6, Option A). Additive: nothing in the mesh path reads slotExprs.
  const slotExprs = {}

  for (const inputDef of NODE_TYPES[outNode.type].inputs) {
    const handle = inputDef.handle
    const target = SLOT_TARGET[handle]   // may be undefined for toon-only controls

    // Reset emit state per slot so each chunk re-emits its FULL subtree into its
    // own GLSL `{}` scope. A node feeding multiple slots (e.g. a Fresnel driving
    // both `emissive` and a Color Ramp `fac`→`color`) is then self-contained in
    // each chunk. Without this its `vec3 v_x = …;` lands only in the first
    // chunk's scope and later chunks hit "undeclared identifier". Var names are
    // deterministic per node:handle, so the re-declaration is identical and the
    // separate scopes never collide.
    stmts.length = 0
    done.clear()
    visiting.clear()
    emittedVarByNodeHandle.clear()

    const edge = incoming(outNode.id, handle)

    const before = stmts.length
    let exprName

    if (edge) {
      const src = byId.get(edge.source)
      if (src) {
        emitNode(src)
        const rec = emittedVarByNodeHandle.get(`${src.id}:${edge.sourceHandle}`)
        if (rec) { exprName = coerce(rec.name, rec.type, inputDef.type) }
      }
    }
    if (!exprName) {
      // Inline constant on the output node, else default. Skip null-default slots.
      const raw = outNode.data?.[handle] ?? inputDef.default
      if (raw == null) continue
      const exposedName = outNode.data?.expose?.[handle]
      if (exposedName) {
        const pn = `p_${sanitize(outNode.id)}_${sanitize(handle)}`
        uniforms[pn] = { value: inputDef.type === 'float' ? Number(raw) : raw, type: inputDef.type }
        if (!params.some(p => p.uniform === pn)) {
          params.push({ uniform: pn, nodeId: outNode.id, handle, type: inputDef.type, name: exposedName, value: inputDef.type === 'float' ? Number(raw) : raw })
        }
        exprName = pn
      } else {
        const name = uniqueName(handle)
        uniforms[name] = { value: inputDef.type === 'float' ? Number(raw) : raw, type: inputDef.type }
        exprName = name
      }
    }

    const newStmts = stmts.slice(before)
    if (target) chunks[handle] = `{\n  ${newStmts.join('\n  ')}\n  ${target(exprName)}\n}`
    slotExprs[handle] = { stmts: newStmts.join('\n  '), expr: exprName, type: inputDef.type }
  }

  return { uniforms, chunks, slotExprs, defines: {}, errors, needsTime, needsUv, needsWorld, needsSplatColor, params, helpers: [...helpers], shaderModel }
}

// Top-level GLSL helper functions, injected once (global scope) when a node
// requests them via ctx.helper(key). Keep names prefixed `aos_` to avoid
// colliding with three.js's own shader functions. Exported so the splat
// param-bake assembler can inject the same helpers into its own shader.
export const HELPER_GLSL = {
  // 2D fractal Perlin noise, ported from Blender's Noise Texture (fBM type):
  //   source/blender/gpu/shaders/material/{gpu_shader_material_fractal_noise,
  //   gpu_shader_material_tex_noise}.glsl.
  // aos_snoise2 = signed Perlin gradient noise ~[-1,1] (Blender's `snoise`).
  // aos_fbm2 sums detail+1 octaves at rising frequency (lacunarity) and falling
  // amplitude (roughness), blends a fractional last octave, then normalizes to
  // [0,1] via 0.5*sum/maxamp+0.5 — exactly Blender's loop. Octaves capped at 8.
  // aos_noise_distort2 domain-warps the coords; aos_rand_off2 makes the seed
  // offsets used for distortion + the per-channel Color outputs.
  noise2: `
float aos_hash21(vec2 p){ p = fract(p * vec2(123.34, 456.21)); p += dot(p, p + 45.32); return fract(p.x * p.y); }
vec2 aos_grad2(vec2 i){ float a = aos_hash21(i) * 6.2831853; return vec2(cos(a), sin(a)); }
float aos_snoise2(vec2 p){
  vec2 i = floor(p), f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  float a = dot(aos_grad2(i + vec2(0.0, 0.0)), f - vec2(0.0, 0.0));
  float b = dot(aos_grad2(i + vec2(1.0, 0.0)), f - vec2(1.0, 0.0));
  float c = dot(aos_grad2(i + vec2(0.0, 1.0)), f - vec2(0.0, 1.0));
  float d = dot(aos_grad2(i + vec2(1.0, 1.0)), f - vec2(1.0, 1.0));
  return 1.4 * mix(mix(a, b, u.x), mix(c, d, u.x), u.y); // raw ~[-0.7,0.7] -> ~[-1,1]
}
vec2 aos_rand_off2(float s){
  return vec2(100.0 + aos_hash21(vec2(s, 0.0)) * 100.0,
              100.0 + aos_hash21(vec2(s, 1.0)) * 100.0);
}
vec2 aos_noise_distort2(vec2 p, float distortion){
  if(distortion == 0.0) return p;
  return p + vec2(aos_snoise2(p + aos_rand_off2(0.0)) * distortion,
                  aos_snoise2(p + aos_rand_off2(1.0)) * distortion);
}
// All five fractal types share one signature (detail, roughness, lacunarity,
// offset, gain, normalize) so the Noise node can dispatch by name. offset/gain
// are unused by fBM; normalize is only honoured by fBM (matches Blender).
float aos_fbm2(vec2 co, float detail, float roughness, float lacunarity, float offset, float gain, float normalize){
  detail = clamp(detail, 0.0, 8.0);
  roughness = max(roughness, 0.0);
  vec2 p = co;
  float fscale = 1.0, amp = 1.0, maxamp = 0.0, sum = 0.0;
  int n = int(detail);
  for(int i = 0; i <= 8; i++){
    if(i > n) break;
    sum    += aos_snoise2(fscale * p) * amp;
    maxamp += amp;
    amp    *= roughness;
    fscale *= lacunarity;
  }
  float rmd = detail - floor(detail);
  bool nrm = normalize != 0.0;
  if(rmd != 0.0){
    float sum2 = sum + aos_snoise2(fscale * p) * amp;
    return nrm ? mix(0.5 * sum / maxamp + 0.5, 0.5 * sum2 / (maxamp + amp) + 0.5, rmd)
               : mix(sum, sum2, rmd);
  }
  return nrm ? 0.5 * sum / maxamp + 0.5 : sum;
}
float aos_multi_fractal2(vec2 co, float detail, float roughness, float lacunarity, float offset, float gain, float normalize){
  detail = clamp(detail, 0.0, 8.0); roughness = max(roughness, 0.0);
  vec2 p = co; float value = 1.0, pwr = 1.0; int n = int(detail);
  for(int i = 0; i <= 8; i++){ if(i > n) break;
    value *= (pwr * aos_snoise2(p) + 1.0); pwr *= roughness; p *= lacunarity; }
  float rmd = detail - floor(detail);
  if(rmd != 0.0) value *= (rmd * pwr * aos_snoise2(p) + 1.0);
  return value;
}
float aos_hetero_terrain2(vec2 co, float detail, float roughness, float lacunarity, float offset, float gain, float normalize){
  detail = clamp(detail, 0.0, 8.0); roughness = max(roughness, 0.0);
  vec2 p = co; float pwr = roughness;
  float value = offset + aos_snoise2(p); p *= lacunarity; int n = int(detail);
  for(int i = 1; i <= 8; i++){ if(i > n) break;
    value += (aos_snoise2(p) + offset) * pwr * value; pwr *= roughness; p *= lacunarity; }
  float rmd = detail - floor(detail);
  if(rmd != 0.0) value += rmd * (aos_snoise2(p) + offset) * pwr * value;
  return value;
}
float aos_hybrid_multi_fractal2(vec2 co, float detail, float roughness, float lacunarity, float offset, float gain, float normalize){
  detail = clamp(detail, 0.0, 8.0); roughness = max(roughness, 0.0);
  vec2 p = co; float pwr = 1.0, value = 0.0, weight = 1.0; int n = int(detail);
  for(int i = 0; i <= 8; i++){
    if(!(weight > 0.001) || i > n) break;
    if(weight > 1.0) weight = 1.0;
    float signal = (aos_snoise2(p) + offset) * pwr; pwr *= roughness;
    value += weight * signal; weight *= gain * signal; p *= lacunarity;
  }
  float rmd = detail - floor(detail);
  if(rmd != 0.0 && weight > 0.001){
    if(weight > 1.0) weight = 1.0;
    value += rmd * weight * (aos_snoise2(p) + offset) * pwr;
  }
  return value;
}
float aos_ridged_multi_fractal2(vec2 co, float detail, float roughness, float lacunarity, float offset, float gain, float normalize){
  detail = clamp(detail, 0.0, 8.0); roughness = max(roughness, 0.0);
  vec2 p = co; float pwr = roughness;
  float signal = offset - abs(aos_snoise2(p)); signal *= signal;
  float value = signal; int n = int(detail);
  for(int i = 1; i <= 8; i++){ if(i > n) break;
    p *= lacunarity;
    float weight = clamp(signal * gain, 0.0, 1.0);
    signal = offset - abs(aos_snoise2(p)); signal *= signal; signal *= weight;
    value += signal * pwr; pwr *= roughness;
  }
  return value;
}`,
  // Perturb a (view-space) geometry normal N by a tangent-space normal mapN,
  // deriving the tangent frame from screen-space derivatives — no precomputed
  // tangents needed (the classic perturbNormal2Arb technique). Used by Normal Map.
  perturbNormal: `
vec3 aos_perturbNormal(vec3 N, vec3 V, vec2 uv, vec3 mapN){
  vec3 q0 = dFdx(V), q1 = dFdy(V);
  vec2 st0 = dFdx(uv), st1 = dFdy(uv);
  vec3 S = normalize(q0 * st1.t - q1 * st0.t);
  vec3 T = normalize(-q0 * st1.s + q1 * st0.s);
  return normalize(mat3(S, T, N) * mapN);
}`,
  // RGB<->HSV, ported from Blender's gpu_shader_common_color_utils.glsl
  // (rgb_to_hsv / hsv_to_rgb). Used by the Hue/Sat/Value node.
  hsv: `
vec3 aos_rgb_to_hsv(vec3 rgb){
  float cmax = max(rgb.r, max(rgb.g, rgb.b));
  float cmin = min(rgb.r, min(rgb.g, rgb.b));
  float cdelta = cmax - cmin;
  float h = 0.0, s = 0.0, v = cmax;
  if (cmax != 0.0) s = cdelta / cmax;
  if (s != 0.0) {
    vec3 c = (vec3(cmax) - rgb) / cdelta;
    if (rgb.r == cmax) h = c.b - c.g;
    else if (rgb.g == cmax) h = 2.0 + c.r - c.b;
    else h = 4.0 + c.g - c.r;
    h /= 6.0;
    if (h < 0.0) h += 1.0;
  }
  return vec3(h, s, v);
}
vec3 aos_hsv_to_rgb(vec3 hsv){
  float h = hsv.x, s = hsv.y, v = hsv.z;
  vec3 rgb;
  if (s == 0.0) { rgb = vec3(v); }
  else {
    if (h == 1.0) h = 0.0;
    h *= 6.0;
    float i = floor(h);
    float f = h - i;
    float p = v * (1.0 - s);
    float q = v * (1.0 - (s * f));
    float t = v * (1.0 - (s * (1.0 - f)));
    if (i == 0.0) rgb = vec3(v, t, p);
    else if (i == 1.0) rgb = vec3(q, v, p);
    else if (i == 2.0) rgb = vec3(p, v, t);
    else if (i == 3.0) rgb = vec3(p, q, v);
    else if (i == 4.0) rgb = vec3(t, p, v);
    else rgb = vec3(v, p, q);
  }
  return rgb;
}`,
}

const SLOT_INCLUDE = {
  color:         '#include <map_fragment>',
  roughness:     '#include <roughnessmap_fragment>',
  metalness:     '#include <metalnessmap_fragment>',
  emissive:      '#include <emissivemap_fragment>',
  normal:        '#include <normal_fragment_maps>',
  opacity:       '#include <alphamap_fragment>',
  // AO is applied after indirect light is accumulated — inject after the aomap chunk.
  ao:            '#include <aomap_fragment>',
  // specular F0 is set up in lights_physical_fragment (Standard + Physical) —
  // overwrite it right after that chunk runs, before the lighting loop reads it.
  specular:      '#include <lights_physical_fragment>',
}

// Shared uniform/varying/helper declarations for both mesh assemblers.
function _graphDecls(compiled) {
  const decls = []
  if (compiled.needsTime) decls.push('uniform float uTime;')
  if (compiled.needsWorld) decls.push('varying vec3 vGraphWorldPos;', 'varying vec3 vGraphWorldNormal;')
  if (compiled.needsSplatColor) decls.push('#ifdef AOS_SPLAT\nvarying vec3 vGaussianColor;\n#else\nvec3 vGaussianColor = vec3(1.0);\n#endif')
  for (const [name, u] of Object.entries(compiled.uniforms ?? {})) {
    let glslType
    if (u.textureId !== undefined) glslType = 'sampler2D'
    else if (u.type && GLSL_TYPE[u.type]) glslType = GLSL_TYPE[u.type]
    else if (Array.isArray(u.value)) glslType = u.value.length === 2 ? 'vec2' : 'vec3'
    else glslType = 'float'
    decls.push(`uniform ${glslType} ${name};`)
  }
  for (const key of compiled.helpers ?? []) {
    if (HELPER_GLSL[key]) decls.unshift(HELPER_GLSL[key])
  }
  return decls
}

// Given the stock fragment shader + a compiled graph, return the patched
// fragment shader: uniform declarations after <common>, and each slot's chunk
// injected immediately after its three.js include line.
export function assembleGraphShader(fragmentShader, compiled) {
  const decls = _graphDecls(compiled)
  let out = fragmentShader.replace('#include <common>', `#include <common>\n${decls.join('\n')}`)
  for (const [slot, chunk] of Object.entries(compiled.chunks ?? {})) {
    const inc = SLOT_INCLUDE[slot]
    if (!inc || !chunk) continue
    out = out.replace(inc, `${inc}\n${chunk}`)
  }
  return out
}

// Patch the stock MeshToon fragment shader with a compiled toon graph. Reuses
// the shared surface chunks (color/normal/emissive/opacity — their SLOT_TARGET
// statements hit variables MeshToon also has) and adds the toon lighting model:
// face-flatten, PMREM env-diffuse (gated by USE_TOON_ENV so the pre-env compile
// is valid), and a fresnel rim. Toon controls come from compiled.slotExprs;
// each is lifted into an `aos_<handle>` var in its own scope to avoid
// cross-control var collisions (the compiler re-emits subtrees per slot).
export function assembleToonShader(fragmentShader, compiled) {
  const decls = _graphDecls(compiled)
  decls.push('uniform sampler2D uToonEnvMap;', 'uniform float uToonEnvIntensity;')
  // textureCubeUV lives in cube_uv_reflection_fragment; three resolves #include
  // before the preprocessor, so wrapping it in #ifdef gates its compilation to
  // when the env (and the CUBEUV_* defines) are present.
  decls.push('#ifdef USE_TOON_ENV\n#include <cube_uv_reflection_fragment>\n#endif')

  let out = fragmentShader.replace('#include <common>', `#include <common>\n${decls.join('\n')}`)

  // Shared surface slots — same include anchors exist in MeshToon.
  for (const slot of ['color', 'normal', 'emissive', 'opacity']) {
    const chunk = compiled.chunks?.[slot]
    const inc = SLOT_INCLUDE[slot]
    if (chunk && inc) out = out.replace(inc, `${inc}\n${chunk}`)
  }

  // Lift each toon control into an aos_<handle> var (own scope per control).
  const lift = (handle, glslType, def) => {
    const se = compiled.slotExprs?.[handle]
    if (!se) return `${glslType} aos_${handle} = ${def};`
    const expr = coerce(se.expr, se.type, glslType)
    return `${glslType} aos_${handle};\n  { ${se.stmts ? se.stmts + '\n    ' : ''}aos_${handle} = ${expr}; }`
  }
  const pre = [
    lift('faceFlatten', 'float', '0.0'),
    lift('diffuseScale', 'float', '1.0'),
    lift('rimColor', 'vec3', 'vec3(1.0)'),
    lift('specIntensity', 'float', '0.5'),
    lift('specFresnelPow', 'float', '4.0'),
  ].join('\n  ')

  // Inject the lift block + face-flatten right after normal_fragment_maps.
  out = out.replace(
    '#include <normal_fragment_maps>',
    // Reassign `normal` only. `geometryNormal` is declared later by three's
    // <lights_fragment_begin> as `vec3 geometryNormal = normal;`, so it inherits
    // the flattened normal automatically — writing it here (before it exists)
    // is an undeclared-identifier compile error in MeshToon (three r0.184).
    `#include <normal_fragment_maps>\n  ${pre}\n  normal = normalize( mix( normal, normalize( vViewPosition ), clamp( aos_faceFlatten, 0.0, 1.0 ) ) );`,
  )

  // Env-diffuse after aomap (indirectDiffuse is final; normal is flattened).
  out = out.replace(
    '#include <aomap_fragment>',
    `#include <aomap_fragment>\n#ifdef USE_TOON_ENV\n  {\n    vec3 aos_wN = inverseTransformDirection( normal, viewMatrix );\n    vec3 aos_env = textureCubeUV( uToonEnvMap, aos_wN, 1.0 ).rgb;\n    reflectedLight.indirectDiffuse += aos_env * diffuseColor.rgb * uToonEnvIntensity * aos_diffuseScale;\n  }\n#endif`,
  )

  // Fresnel rim added to outgoingLight before opaque_fragment.
  out = out.replace(
    '#include <opaque_fragment>',
    `  {\n    float aos_rim = pow( 1.0 - saturate( dot( normalize( normal ), normalize( vViewPosition ) ) ), max( aos_specFresnelPow, 0.001 ) );\n    outgoingLight += aos_rimColor * aos_rim * aos_specIntensity;\n  }\n#include <opaque_fragment>`,
  )

  return out
}

// Patch the stock vertex shader to compute + pass the world-space varyings the
// World Position / World Normal nodes read. No-op unless compiled.needsWorld.
// `objectNormal` is defined by <beginnormal_vertex>; `transformed` by
// <begin_vertex>. Both anchors exist in every MeshStandard vertex shader.
export function assembleGraphVertex(vertexShader, compiled) {
  if (!compiled.needsWorld) return vertexShader
  let out = vertexShader.replace(
    '#include <common>',
    '#include <common>\nvarying vec3 vGraphWorldPos;\nvarying vec3 vGraphWorldNormal;',
  )
  out = out.replace(
    '#include <beginnormal_vertex>',
    '#include <beginnormal_vertex>\nvGraphWorldNormal = normalize(mat3(modelMatrix) * objectNormal);',
  )
  out = out.replace(
    '#include <begin_vertex>',
    '#include <begin_vertex>\nvGraphWorldPos = (modelMatrix * vec4(transformed, 1.0)).xyz;',
  )
  return out
}
